import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Apple, Play, Globe } from "lucide-react";
import { Logo } from "../landing/Logo";
import { Reveal } from "../landing/Reveal";

const EXP_URL =
  "exp://study-social-eat.preview.emergentagent.com?expo_go_prompt_device_auth=1&expo_go_device_auth_verification_uri_override=app.emergent.sh";

const SCREENS = [
  { title: "Learn Skills", sub: "Build Your Future", grad: "from-[#635BFF] to-[#8B85FF]" },
  { title: "Earn Money", sub: "Become A Creator", grad: "from-[#00D26A] to-[#4ADE80]" },
  { title: "Start A Business", sub: "Sell Food · Sell Products", grad: "from-[#FF7A00] to-[#FFB25E]" },
  { title: "Get Jobs", sub: "Access Opportunities", grad: "from-[#06B6D4] to-[#67E8F9]" },
  { title: "Join The FUNZ Community", sub: "Learn · Earn · Build · Belong", grad: "from-[#EC4899] to-[#F472B6]" },
];

const STORES = [
  { icon: Play, label: "Google Play", href: "/qr" },
  { icon: Apple, label: "Apple App Store", href: "/qr" },
  { icon: Globe, label: "Web Platform", href: "/qr" },
];

export const DownloadApp = () => {
  const [qr, setQr] = useState(null);
  const [screen, setScreen] = useState(0);

  useEffect(() => {
    QRCode.toDataURL(EXP_URL, { width: 512, margin: 1 }).then(setQr).catch(() => {});
    const t = setInterval(() => setScreen((s) => (s + 1) % SCREENS.length), 2600);
    return () => clearInterval(t);
  }, []);

  const s = SCREENS[screen];

  return (
    <section id="download" data-testid="download-section" className="relative overflow-hidden py-24 sm:py-32">
      <div aria-hidden="true" className="absolute left-1/2 top-1/2 h-[480px] w-[760px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#635BFF]/[0.09] blur-[160px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          <Reveal className="flex justify-center">
            <div
              data-testid="onboarding-phone"
              className="w-[270px] rounded-[2.8rem] border border-white/10 bg-[#111827] p-2.5 shadow-[0_40px_120px_-30px_rgba(99,91,255,0.45)] sm:w-[290px]"
            >
              <div className="relative overflow-hidden rounded-[2.3rem] bg-[#09090B]">
                <div className="flex items-center justify-between px-6 pt-4 text-white">
                  <span className="text-[11px] font-semibold">9:41</span>
                  <span className="h-[16px] w-[64px] rounded-full bg-[#111827]" aria-hidden="true" />
                </div>
                <div className="flex h-[380px] flex-col items-center justify-center px-8 pb-10 text-center">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={screen}
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -24 }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className="flex flex-col items-center"
                      data-testid="onboarding-screen"
                    >
                      <span className={`flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br ${s.grad}`}>
                        <Logo className="h-8 w-8" />
                      </span>
                      <p className="mt-6 font-display text-2xl font-black tracking-tight text-white">
                        {s.title}
                      </p>
                      <p className="mt-2 text-sm text-slate-400">{s.sub}</p>
                    </motion.div>
                  </AnimatePresence>
                  <div className="absolute bottom-8 flex gap-1.5">
                    {SCREENS.map((_, i) => (
                      <span
                        key={i}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          i === screen ? "w-6 bg-[#00D26A]" : "w-1.5 bg-white/15"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <p className="font-jbmono text-xs uppercase tracking-[0.3em] text-[#00D26A]">
              Download App
            </p>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Your campus OS, always in your pocket.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400 sm:text-lg">
              Available on every platform you already live on. Scan the QR to open
              the live preview instantly.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              {STORES.map((st) => (
                <Link
                  key={st.label}
                  to={st.href}
                  data-testid={`store-${st.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center gap-2.5 rounded-full border border-white/10 bg-[#111827] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:border-[#635BFF]/60"
                >
                  <st.icon className="h-4 w-4 text-[#00D26A]" />
                  {st.label}
                </Link>
              ))}
            </div>
            <div className="mt-9 flex items-center gap-5">
              <div className="rounded-2xl bg-white p-2.5">
                {qr ? (
                  <img
                    src={qr}
                    alt="QR code to open the FUNZ app preview"
                    data-testid="download-qr"
                    className="h-28 w-28"
                  />
                ) : (
                  <div className="h-28 w-28 animate-pulse rounded-lg bg-neutral-200" />
                )}
              </div>
              <p className="max-w-[200px] text-sm leading-relaxed text-slate-400">
                Scan with your phone camera to open the live FUNZ preview.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-20 text-center">
            <p className="font-jbmono text-xs uppercase tracking-[0.3em] text-[#00D26A]">
              Live Demo
            </p>
            <h3 className="mx-auto mt-3 max-w-xl font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
              Don't just read about it. Tap around in it.
            </h3>
            <div className="mt-10 flex justify-center">
              <div
                data-testid="live-demo-frame"
                className="w-[320px] rounded-[2.8rem] border border-white/10 bg-[#111827] p-2.5 shadow-[0_40px_120px_-30px_rgba(0,210,106,0.35)] sm:w-[360px]"
              >
                <div className="overflow-hidden rounded-[2.3rem] bg-[#09090B]">
                  <div className="flex items-center justify-between px-6 pt-4 text-white">
                    <span className="text-[11px] font-semibold">9:41</span>
                    <span className="h-[16px] w-[64px] rounded-full bg-[#111827]" aria-hidden="true" />
                  </div>
                  <iframe
                    src="https://study-social-eat.preview.emergentagent.com"
                    title="FUNZ app live demo"
                    data-testid="live-demo-iframe"
                    className="mt-2 h-[560px] w-full bg-white"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
            <Link
              to="/qr"
              data-testid="live-demo-open-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3 font-display text-xs font-bold uppercase tracking-wider text-slate-200 transition-colors hover:border-[#635BFF]/60 hover:text-white"
            >
              Open on your phone via QR
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
